import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import { Bookmark, Trash2, MessageCircle, Sparkles, Loader2, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n/I18nProvider";
import { SavedQuestions } from "@/components/SavedQuestions";

export default function BookmarksPage() {
  const { token } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchBookmarks(); }, []);

  const fetchBookmarks = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookmarks`, { headers });
      setBookmarks(data.bookmarks);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const removeBookmark = async (id) => {
    try {
      await axios.delete(`${API}/bookmarks/${id}`, { headers });
      setBookmarks(prev => prev.filter(b => b.id !== id));
      toast.success("Bookmark removed");
    } catch (err) { toast.error("Failed to remove"); }
  };

  const goToConversation = (convId) => {
    navigate(`/chat/${convId}`);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 py-8 space-y-6">
          <div>
            <h1 className="font-heading text-3xl font-medium mb-2">Bookmarks</h1>
            <p className="text-muted-foreground text-sm">
              {bookmarks.length} saved message{bookmarks.length !== 1 ? "s" : ""}
            </p>
          </div>

          {bookmarks.length === 0 ? (
            <div className="card-omni p-12 text-center">
              <Bookmark className="w-12 h-12 text-muted-foreground/20 mx-auto mb-4" strokeWidth={1.5} />
              <h2 className="font-heading text-xl font-medium mb-2">No bookmarks yet</h2>
              <p className="text-muted-foreground text-sm">
                Bookmark messages from your conversations that you want to revisit later.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {bookmarks.map(bm => (
                <div
                  key={bm.id}
                  data-testid={`bookmark-${bm.id}`}
                  className="card-omni p-5 group hover:border-primary/20 transition-all"
                >
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={cn(
                        "w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs",
                        bm.role === "user"
                          ? "bg-primary/10 text-primary"
                          : "bg-blue-100 dark:bg-blue-900/30 text-blue-600"
                      )}>
                        {bm.role === "user" ? "Y" : <Sparkles className="w-3.5 h-3.5" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-muted-foreground truncate">
                          {bm.role === "user" ? "You" : "Omni"} &middot; {bm.conversation_title}
                        </p>
                        {bm.timestamp && (
                          <p className="text-[10px] text-muted-foreground/60 ui-label">
                            {bm.timestamp.slice(0, 16).replace("T", " ")}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        data-testid={`goto-bookmark-${bm.id}`}
                        onClick={() => goToConversation(bm.conversation_id)}
                        className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-primary transition-all"
                        title="Go to conversation"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`remove-bookmark-${bm.id}`}
                        onClick={() => removeBookmark(bm.id)}
                        className="p-1.5 rounded-lg hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"
                        title="Remove bookmark"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap line-clamp-4">
                    {bm.content}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Saved Question Bookmarks */}
          <div className="border-t border-border/30 pt-6">
            <SavedQuestions />
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
